<template>
  <transition :name="$style.fade">
    <div
      v-if="isVisible"
      :class="[
        $style.announcement,
        [$style[type]]
      ]"
    >
      <div
        :class="caretClasses"
        :style="cssProps"
      />
      <header :class="$style.header">
        <div>
          <h4
            v-if="intro"
            :class="$style.heading"
          >
            {{ intro }}
          </h4>
          <h4 :class="$style.heading">
            {{ header }}
          </h4>
        </div>
        <CloseButton
          :class="$style.closeButton"
          :handleClose="closeAnnouncement"
        />
      </header>

      <p :class="$style.text">
        {{ text }}
      </p>
      <slot />

      <footer
        v-if="showFooter"
        :class="$style.buttons"
      >
        <FlatActionAnchor
          v-if="mainActionLink"
          :buttonStyles="[$style.button]"
          :href="mainActionLink"
          :text="mainActionText"
          @click="handleActionButtonClick"
        />
        <FlatActionButton
          v-else
          :action="handleActionButtonClick"
          :buttonClass="[$style.button]"
          :text="mainActionText"
        />
        <FlatActionButton
          v-if="showDismiss"
          :action="handleDismissButtonClick"
          :buttonClass="[$style.link]"
          data-test-id="Announcement__dismiss"
          :text="dismissText"
        />
      </footer>
    </div>
  </transition>
</template>

<script>
import { eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import AnnouncementMixin from 'client/components/Announcement/AnnouncementMixin';
import CloseButton from 'components/Buttons/CloseButton.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionAnchor from 'components/Buttons/FlatActionAnchor.vue';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import SessionStorageService from 'client/util/SessionStorageService';

const {
  description, iconName, location, text,
} = eventProperties;

export default {
  name: 'Announcement',

  components: {
    FlatActionAnchor,
    FlatActionButton,
    CloseButton,
  },

  mixins: [AnnouncementMixin],

  props: {
    shouldShowForAdmin: {
      type: Boolean,
      default: false,
    },
  },

  LocalStorageService,
  SessionStorageService,

  data() {
    return {
      isVisible: false,
      dismissText: 'dismiss forever',
    };
  },

  computed: {

    caretClasses() {
      const customClasses = this.caretPointsTo
      // caretPointsTo allows developer to choose what side caret appears on announcement
        ? {
          [this.$style[`caretPointsTo-${this.caretPointsTo}`]]: true,
        }
        // historic method for caret positioning, only allowing for top and bottom
        // caretLeft or caretRight positions caret 60px for side of announcement.
        : {
          [this.$style.caretLeft]: this.caretPosition === 'left',
          [this.$style.caretRight]: this.caretPosition === 'right',
          [this.$style.caretBottom]: this.caretBottom,
        };

      return {
        [this.$style.caret]: true,
        [this.customCaretClassName]: this.customCaretClassName,
        ...customClasses,
      };
    },

    cssProps() {
      // positive values are used from [top, left]
      // negative values are used from [bottom, right]
      return {
        '--caret-offset-start': this.caretOffset < 0 ? 'auto' : `${this.caretOffset}px`,
        '--caret-offset-end': this.caretOffset < 0 ? `${Math.abs(this.caretOffset)}px` : 'auto',
      };
    },
  },

  EventHelper,

  mounted() {
    this.openAnnouncement();
    this.dispatchModalViewed();
  },

  methods: {
    closeAnnouncement() {
      if (this.showDismiss) {
        const { setItem } = this.$options.SessionStorageService;
        this.isVisible = false;
        if (this.localStorageKey) {
          setItem(this.localStorageKey, 'seen');
        }
      } else {
        this.dismissAnnouncement();
      }
      this.dispatchCloseEvent();
    },

    dismissAnnouncement() {
      const { setItem } = this.$options.LocalStorageService;

      this.isVisible = false;

      if (this.localStorageKey) {
        setItem(this.localStorageKey, 'seen');
      }
    },

    dispatchCloseEvent() {
      if (this.eventLocation) {
        this.$options.EventHelper.triggerIconClicked({
          [location]: this.eventLocation,
          [description]: `${this.eventLocation} Popover Closed`,
          [iconName]: 'close_modal',
        });
      }
    },

    dispatchDismissEvent() {
      if (this.eventLocation) {
        this.$options.EventHelper.triggerButtonClicked({
          [location]: this.eventLocation,
          [description]: `${this.eventLocation} Popover Dismissed`,
          [text]: this.dismissText,
        });
      }
    },

    dispatchMainActionEvent() {
      if (this.eventLocation) {
        this.$options.EventHelper.triggerButtonClicked({
          [location]: this.eventLocation,
          [description]: `${this.eventLocation} Main Action Clicked`,
          [text]: this.mainActionText,
        });
      }
    },

    dispatchModalViewed() {
      if (this.eventLocation) {
        this.$options.EventHelper.triggerModalViewed({
          [location]: this.eventLocation,
          [description]: `${this.eventLocation} Popover Viewed`,
        });
      }
    },

    handleActionButtonClick() {
      this.dispatchMainActionEvent();
      this.dismissAnnouncement();
      this.$emit('main-action');
    },

    handleDismissButtonClick() {
      this.dispatchDismissEvent();
      this.dismissAnnouncement();
      this.$emit('dismiss-action');
    },

    openAnnouncement() {
      const { getValue } = this.$options.LocalStorageService;
      const getSessionItem = this.$options.SessionStorageService.getItem;

      setTimeout(() => {
        this.isVisible = (!this.localStorageKey
        || (!getValue(this.localStorageKey) && !getSessionItem(this.localStorageKey)))
          && this.shouldShowForAdmin;
      }, this.delay);
    },
  },
};
</script>

<style scoped module>
@value announcementZIndex from '~css/z-index.css';
@value (jcInformationalBase, jcGrey800) from '~css/brand-colors.css';
@value slowEaseFade from '~css/transitions.css';
@value arrowTop: 50% 0, 0 100%, 100% 100%;
@value shadow1: 0 19px 38px rgba(0, 0, 0, 0.3);
@value shadow2: 0 15px 12px rgba(0, 0, 0, 0.22);

.announcement {
  border-radius: 5px;
  color: var(--announcement-color);
  padding: 24px;
  position: absolute;
  width: 40ch;
  z-index: announcementZIndex;
}

.informational {
  --announcement-color: white;
  --button-color: var(--announcement-color);
  --announcement-background: jcInformationalBase;
}

.light {
  --button-color: jcInformationalBase;
  --announcement-color: jcGrey800;
  --announcement-background: white;

  filter: drop-shadow(shadow1) drop-shadow(shadow2);
}

.announcement,
.caret {
  background: var(--announcement-background);
}

.caret {
  --caret-width: 18px;
  --caret-height: 12px;

  clip-path: polygon(arrowTop);
  height: var(--caret-height);
  margin-top: 1px;
  position: absolute;
  top: -12px;
  width: var(--caret-width);
}

.caretPointsTo-top {
  left: var(--caret-offset-start);
  right: var(--caret-offset-end);
}

.caretPointsTo-right {
  bottom: calc(var(--caret-offset-end) - calc(var(--caret-height) / 2));
  right: -15px;
  top: calc(var(--caret-offset-start) + calc(var(--caret-height) / 2));
  transform: rotate(90deg);
}

.caretPointsTo-bottom {
  left: var(--caret-offset-start);
  right: var(--caret-offset-end);
  top: calc(100% - 1px);
  transform: rotate(180deg);
}

.caretPointsTo-left {
  bottom: calc(var(--caret-offset-end) - calc(var(--caret-height) / 2));
  left: -15px;
  top: calc(var(--caret-offset-start) + calc(var(--caret-height) / 2));
  transform: rotate(270deg);
}

.caretBottom {
  top: calc(100% - 1px);
  transform: rotate(180deg);
}

.caretLeft {
  left: 60px;
}

.caretRight {
  right: 60px;
}

.header {
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr auto;
}

.closeButton {
  color: var(--button-color);
  transition: color 0.3s ease-in-out;
}

.closeButton:hover {
  color: var(--announcement-background);
}

.heading.heading {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 1rem;
}

.text {
  margin: 0 0 1rem;
}

.buttons {
  display: flex;
  margin-top: 2rem;
}

.button.button {
  background: var(--button-color);
  color: var(--announcement-background) !important;
  cursor: pointer;
}

.button.button:focus,
.button.button:hover {
  text-decoration: none;
}

.link {
  background: none;
  color: white;
  cursor: pointer;
  font-weight: normal;
  margin-left: auto;
  text-decoration: underline;
  width: auto;
}

.fade,
.fade-enter-active,
.fade-leave-active {
  transition: slowEaseFade;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
